import {
  getShortcutModifiers,
  isDisabled,
  sentryComponentReporter,
  shortcutLabel
} from './helpers';

/*******************************************************************************
 * shortcutClick */
/**
 * This directive allows for clicking on an element using keyboard shortcuts.
 * Typically, this will be used on button, though any element that can be
 * clicked should work as well.
 *******************************************************************************/
export default function shortcutClick() {
  /* @ngInject */ function clickController($element, $injector, $scope) {
    var $ctrl = this;
    var $svcs = { $ctrl, $element, $injector, $scope };

    $ctrl.$onInit = $injector.invoke(
      initCtrl,
      null,
      Object.assign($svcs, { $svcs })
    );
  }

  /*****************************************************************************
   * initCtrl */
  /**
   * Initializes the controller object with data and functions.
   *****************************************************************************/
  /* @ngInject */ function initCtrl(
    $ctrl,
    $document,
    $element,
    $injector,
    $scope,
    $svcs,
    $timeout,
    SHORTCUT_CONSTANTS
  ) {
    return function () {
      Object.assign($ctrl, {
        /*--------------------------------------------*/
        /* We're making targetKey a function so that  */
        /* any angular code in the attribute has time */
        /* to be converted to the expression's value. */
        /*--------------------------------------------*/
        getTargetKey: () =>
          $element[0].hasAttribute(SHORTCUT_CONSTANTS.ATTR_CLICK)
            ? $element[0].getAttribute(SHORTCUT_CONSTANTS.ATTR_CLICK)
            : null,
        isLabelHidden: () =>
          $element[0].hasAttribute(SHORTCUT_CONSTANTS.ATTR_HIDE_LABEL) &&
          $element[0].getAttribute(SHORTCUT_CONSTANTS.ATTR_HIDE_LABEL) !==
            'false',

        getShortcutModifiers: $injector.invoke(
          getShortcutModifiers,
          null,
          $svcs
        ),
        isDisabled: $injector.invoke(isDisabled, null, $svcs),
        keyListener: $injector.invoke(keyListener, null, $svcs),
        sentryComponentReporter: $injector.invoke(
          sentryComponentReporter,
          null,
          $svcs
        ),
        shortcutLabel: $injector.invoke(shortcutLabel, null, $svcs),
        updateText: $injector.invoke(updateText, null, $svcs)
      });

      Object.assign($ctrl, $ctrl.getShortcutModifiers());

      /*-----------------*/
      /* Set up listener */
      /*-----------------*/
      $document.on('keydown', $ctrl.keyListener);
      $scope.$on('$destroy', () => {
        $document.off('keydown', $ctrl.keyListener);
      });

      $timeout($ctrl.updateText, 50);
    };
  }

  /*****************************************************************************
   * keyListener */
  /**
   * Key press listener for the provided shortcut. When triggered by the shortcut
   * key, the click listeners will be triggered on the element.
   *****************************************************************************/
  /* @ngInject */ function keyListener($ctrl, $element, $timeout, KEYCODES) {
    return function (event) {
      try {
        /*-------------------------------------------------------*/
        /* If the user clicked a key that is not defined, we do  */
        /* not want to continue. This has been seen in Microsoft */
        /* Edge when hitting Tab to autofill an email address.   */
        /*-------------------------------------------------------*/
        if (
          event.keyCode === null ||
          event.keyCode === undefined ||
          $ctrl.isDisabled()
        )
          return;

        /*----------------------------------------------*/
        /* Make sure if one or more of the modifier     */
        /* keys are required, that they are being used. */
        /*----------------------------------------------*/
        if ($ctrl.modAlt != event.altKey) return;
        if ($ctrl.modCtrl != event.ctrlKey) return;
        if ($ctrl.modShift != event.shiftKey) return;

        /*---------------------------------------------------*/
        /* Check that the pressed key matches the targetKey. */
        /*---------------------------------------------------*/
        const code = event.keyCode;
        const targetKey = ($ctrl.getTargetKey() || '').toUpperCase();
        if (KEYCODES.LETTERS[targetKey] !== code) {
          return;
        }

        event.preventDefault();

        $timeout(function () {
          if ($element[0] && !$element[0].disabled) {
            $element.trigger('click');
          }
        }, 150);
      } catch (error) {
        $ctrl.sentryComponentReporter(error, { keyCodeEvent: event });
      }
    };
  }

  /*****************************************************************************
   * updateText */
  /**
   * Update the text with the shortcut value. Skipped if the shortcut-hide-label
   * attribute is true.
   *****************************************************************************/
  /* @ngInject */ function updateText($ctrl, $element) {
    const element = $element[0];

    /*---------------*/
    /* Element types */
    /*---------------*/
    const TYPE_A = 'A';
    const TYPE_BUTTON = 'BUTTON';

    return function () {
      if ($ctrl.isLabelHidden()) {
        return;
      }

      let currentText = null;

      /*------------------------------------------------*/
      /* We are using innerHTML here so that any        */
      /* glyphicons in internal spans will be retained. */
      /*------------------------------------------------*/
      switch (element.nodeName) {
        case TYPE_A:
        case TYPE_BUTTON:
          currentText = element.innerHTML;
          break;
        default:
          console.error('Type unknown: ', element.nodeName);
      }

      const shortcutText = `${currentText} ${$ctrl.shortcutLabel()}`;
      switch (element.nodeName) {
        case TYPE_A:
        case TYPE_BUTTON:
          element.innerHTML = shortcutText;
          break;
      }
    };
  }

  /*------------------------------*/
  /* Return the directive object. */
  /*------------------------------*/
  return {
    restrict: 'A',
    controller: ['$element', '$injector', '$scope', clickController]
  };
}
